import type {
  AnnotatedJsonSchema,
  EntityControlType,
  SchemaTransformer,
} from '@web-config-app/core';

/**
 * Auto-applies `x-entity-presentation.omitFromTree: true` to any property
 * declaring an `x-entity-control.type` that owns its own subtree rendering.
 * The tree builder reads this annotation and skips matching properties so
 * they don't appear as sub-page nodes in the entity nav.
 *
 * Add more values here as new self-contained controls are introduced.
 * Authors can also set `omitFromTree: true` explicitly; this transformer
 * never overwrites an existing value.
 *
 * Operates on the CURRENT schema level only: it annotates matching direct
 * properties and relies on `computeSchema`/`traverseSchema` to re-run the
 * whole transformer chain at every nested level as it recurses — the same
 * per-level pattern sibling transformers (e.g. `addObjectCardAnnotation`)
 * use.
 */

// `EntityControlType` typing makes a control-type rename a build error here.
const SELF_CONTAINED_CONTROL_TYPES: ReadonlySet<string> =
  new Set<EntityControlType>(['conditionalAction']);

export const applyOmitFromTreeAnnotation: SchemaTransformer = ({ schema }) => {
  if (!schema || typeof schema !== 'object') return schema;

  const { properties } = schema;
  if (!properties || typeof properties !== 'object') return schema;

  Object.values(properties).forEach((property) => {
    // `properties` values are loosely typed; treat each as an annotated
    // schema. Every field access below is optional-chained, so a
    // non-conforming entry simply matches nothing rather than throwing.
    const propertySchema = property as AnnotatedJsonSchema;
    const controlType = propertySchema['x-entity-control']?.type;
    if (
      controlType &&
      SELF_CONTAINED_CONTROL_TYPES.has(controlType) &&
      // Only apply when the author hasn't declared a value at all — an
      // explicit `omitFromTree: false` must be respected, so a falsy
      // check is not enough.
      propertySchema['x-entity-presentation']?.omitFromTree === undefined
    ) {
      propertySchema['x-entity-presentation'] = {
        ...(propertySchema['x-entity-presentation'] ?? {}),
        omitFromTree: true,
      };
    }
  });

  return schema;
};
